import React from "react";
import { SpaceBetween, SplitPanel, Table } from "@amzn/awsui-components-react";
import { formatDateValueFromDB } from "utils/date-format";
import {
  ChangeLog,
  statusUpdateFields,
  customAttributeFieldNames,
} from "components/activity-logs/activity-log/definitions";
import { ActivityLogSplitPanelProps } from "./definitions";
import {
  getUIFieldName,
  isDateField,
  parseCustomAttributes,
  parseReopenHistory,
  prettyPrintTeamJson,
} from "./utils";
import {
  TEAM_CONFIG_CATEGORIES,
  TEAM_JSON_BLOB_FIELDS,
} from "./field-mappings";
import {
  formatGuestLabel,
  isOwnerGuestLabelKey,
} from "utils/guest-label-utils";
import { useTeamGuestAliases } from "hooks/useTeamGuestAliases";
import styles from "./style.module.scss";

const ActivityLogSplitPanel: React.FC<ActivityLogSplitPanelProps> = (
  props: ActivityLogSplitPanelProps,
) => {
  const guestAliases = useTeamGuestAliases();

  const formatValue = (
    attributeName: string,
    value: string,
  ): React.ReactNode => {
    if (!value) return "";
    if (isDateField(attributeName)) {
      return formatDateValueFromDB(value);
    }
    if (
      TEAM_CONFIG_CATEGORIES.has(props.item?.category ?? "") &&
      TEAM_JSON_BLOB_FIELDS.has(attributeName)
    ) {
      return (
        <pre className={styles.teamConfigJson}>
          {prettyPrintTeamJson(value)}
        </pre>
      );
    }
    // Historical values still get the current-state guest suffix — see
    // isOwnerGuestLabelKey for the rationale and the name-shape normalization.
    if (isOwnerGuestLabelKey(attributeName)) {
      return formatGuestLabel(value, guestAliases);
    }
    return value;
  };

  const splitPanelTable = () => {
    if (!props.item) return null;

    const allChanges: ChangeLog[] = [];

    props.item.changeLogs.forEach((log) => {
      if (log.attributeName === "reopen_history") {
        const latestEntry = parseReopenHistory(log.oldValue, log.newValue);
        if (latestEntry) {
          allChanges.push({
            attributeName: "reopen_reason",
            oldValue: "",
            newValue: latestEntry.reason,
          });
          allChanges.push({
            attributeName: "reopened_by",
            oldValue: "",
            newValue: latestEntry.reopenedBy,
          });
          allChanges.push({
            attributeName: "reopened_at",
            oldValue: "",
            newValue: latestEntry.reopenedAt,
          });
        }
      } else if (customAttributeFieldNames.includes(log.attributeName)) {
        // Parse the custom attributes string into an object
        try {
          const oldAttributes = parseCustomAttributes(log.oldValue);
          const newAttributes = parseCustomAttributes(log.newValue);

          const allKeys = new Set([
            ...Object.keys(oldAttributes),
            ...Object.keys(newAttributes),
          ]);

          allKeys.forEach((key) => {
            const oldVal = oldAttributes[key] || "-";
            const newVal = newAttributes[key] || "-";

            // Only show changed values
            if (oldVal !== newVal) {
              // For progress updates, only show status-related fields
              if (props.item!.category === "Progress update") {
                if (statusUpdateFields.includes(key)) {
                  allChanges.push({
                    attributeName: `custom_${key}`,
                    oldValue: oldVal,
                    newValue: newVal,
                  });
                }
              } else {
                // For non-progress updates, show all changed custom attributes
                allChanges.push({
                  attributeName: `custom_${key}`,
                  oldValue: oldVal,
                  newValue: newVal,
                });
              }
            }
          });
        } catch (error) {
          console.error("Error parsing custom attributes:", error);
        }
      } else {
        allChanges.push(log);
      }
    });

    // Sort the combined changes
    const filteredChangeLogs = allChanges.sort((a, b) => {
      const fieldA = getUIFieldName(a.attributeName, props.item!.category);
      const fieldB = getUIFieldName(b.attributeName, props.item!.category);
      return fieldA.localeCompare(fieldB);
    });

    const getColumnDefinitions = () => {
      const baseColumns = [
        {
          id: "attribute",
          header: "Field",
          cell: (item: ChangeLog) =>
            getUIFieldName(item.attributeName, props.item!.category),
          width: "20%",
        },
      ];

      const previousValueCol = {
        id: "oldValue",
        header: "Previous value",
        cell: (item: ChangeLog) =>
          formatValue(item.attributeName, item.oldValue),
        width: "40%",
      };

      const newValueCol = {
        id: "newValue",
        header: "Updated value",
        cell: (item: ChangeLog) =>
          formatValue(item.attributeName, item.newValue),
        width: "40%",
      };

      if (props.item!.operation === "create") {
        return [...baseColumns, newValueCol];
      }
      if (props.item!.operation === "delete") {
        return [...baseColumns, previousValueCol];
      }
      return [...baseColumns, newValueCol, previousValueCol];
    };

    return (
      <div>
        <h3>Details</h3>
        <SpaceBetween size="l">
          <Table
            items={filteredChangeLogs}
            columnDefinitions={getColumnDefinitions()}
            variant="borderless"
            sortingDisabled
            wrapLines
            stickyHeader
          />
        </SpaceBetween>
      </div>
    );
  };

  return (
    <SplitPanel
      header={props.item ? `${props.item.headerDate}` : "No log item selected"}
      i18nStrings={{
        closeButtonAriaLabel: "Close panel",
        openButtonAriaLabel: "Open panel",
        preferencesTitle: "Preferences",
        preferencesPositionLabel: "Position",
        preferencesPositionDescription:
          "Choose the default position of the split panel",
        preferencesPositionSide: "Side",
        preferencesPositionBottom: "Bottom",
        preferencesCancel: "Cancel",
        preferencesConfirm: "Confirm",
      }}
      closeBehavior={props.isActivitiesTab ? "collapse" : "hide"}
    >
      {splitPanelTable()}
    </SplitPanel>
  );
};

export default ActivityLogSplitPanel;
